import arbitrumInactive from '@assets/chains/arbitrum-inactive.svg?url';
import arbitrum from '@assets/chains/arbitrum.svg?url';
import avalancheInactive from '@assets/chains/avalanche-inactive.svg?url';
import avalanche from '@assets/chains/avalanche.svg?url';
import baseInactive from '@assets/chains/base-inactive.svg?url';
import base from '@assets/chains/base.svg?url';
import binanceInactive from '@assets/chains/bnb-inactive.svg?url';
import binance from '@assets/chains/bnb.svg?url';
import mantaInactive from '@assets/chains/manta-inactive.svg?url';
import mantaWhite from '@assets/chains/manta-white.svg?url';
import manta from '@assets/chains/manta.svg?url';
import optimismInactive from '@assets/chains/optimism-inactive.svg?url';
import optimism from '@assets/chains/optimism.svg?url';
import pulseInactive from '@assets/chains/pulse-inactive.svg?url';
import pulseWhite from '@assets/chains/pulse-white.svg?url';
import pulse from '@assets/chains/pulse.svg?url';
import sonicInactive from '@assets/chains/sonic-inactive.svg?url';
import sonic from '@assets/chains/sonic.svg?url';

import { ChainInfo, ChainMapped } from '@app-types/common/token.types';

export enum CHAIN_ID {
  BSC = 56,
  OPTIMISM = 10,
  ARBITRUM = 42161,
  AVALANCHE = 43114,
  BASE = 8453,
  PULSE = 369,
  MANTA = 169,
  SONIC = 146,
}

export const AVAILABLE_CHAINS_LIST = [
  // CHAIN_ID.BSC,
  // CHAIN_ID.OPTIMISM,
  // CHAIN_ID.ARBITRUM,
  // CHAIN_ID.AVALANCHE,
  CHAIN_ID.BASE,
  // CHAIN_ID.PULSE,
  // CHAIN_ID.MANTA,
  // CHAIN_ID.SONIC,
] as const;

export type AvailableChainIdType = (typeof AVAILABLE_CHAINS_LIST)[number];

export const DEFAULT_CAIN_ID = CHAIN_ID.BASE;

export const CHAINS_RPC = {
  [CHAIN_ID.BSC]: [
    'https://bsc-rpc.publicnode.com',
    'https://binance.llamarpc.com',
    'https://bsc-mainnet.public.blastapi.io',
  ],
  [CHAIN_ID.OPTIMISM]: [
    'https://optimism-rpc.publicnode.com',
    'https://optimism.llamarpc.com',
    'https://rpc.ankr.com/optimism',
    'https://1rpc.io/op',
    'https://rpc.optimism.gateway.fm',
  ],
  [CHAIN_ID.ARBITRUM]: [
    'https://arb1.arbitrum.io/rpc',
    'https://arbitrum-one-rpc.publicnode.com',
    'https://arbitrum-one.public.blastapi.io',
    'https://rpc.ankr.com/arbitrum',
    'https://arbitrum.drpc.org',
    'https://arb-pokt.nodies.app',
  ],
  [CHAIN_ID.AVALANCHE]: [
    'https://avalanche-c-chain-rpc.publicnode.com',
    'https://avalanche.public-rpc.com',
    'https://rpc.ankr.com/avalanche',
    'https://1rpc.io/avax/c',
  ],
  [CHAIN_ID.BASE]: [
    'https://base-rpc.publicnode.com',
    'https://mainnet.base.org',
    'https://1rpc.io/base',
    'https://base-mainnet.public.blastapi.io',
    'https://base-pokt.nodies.app',
    'https://base.drpc.org',
    'https://base.meowrpc.com',
  ],
  [CHAIN_ID.PULSE]: [
    'https://rpc.pulsechain.com',
    'https://pulsechain-rpc.publicnode.com',
    'https://rpc-pulsechain.g4mm4.io',
    'https://evex.cloud/pulserpc',
  ],
  [CHAIN_ID.MANTA]: [
    'https://manta-pacific.drpc.org',
    'https://pacific-rpc.manta.network/http',
    'https://1rpc.io/manta',
    'https://manta-pacific-gascap.calderachain.xyz/http',
  ],
  [CHAIN_ID.SONIC]: [
    'https://sonic.drpc.org',
    'https://rpc.ankr.com/sonic_mainnet',
    'https://rpc.soniclabs.com',
  ],
};

export const COLOR_MAP = {
  [CHAIN_ID.BSC]: '#F0B90B',
  [CHAIN_ID.OPTIMISM]: '#FF0420',
  [CHAIN_ID.ARBITRUM]: '#2C374B',
  [CHAIN_ID.AVALANCHE]: '#E84142',
  [CHAIN_ID.BASE]: '#FFFFFF',
  [CHAIN_ID.MANTA]: '#0067B7',
  [CHAIN_ID.PULSE]: '#90009C',
  [CHAIN_ID.SONIC]: '#E3E3E3',
};

export const CHAINS_NAME: Record<CHAIN_ID, string> = {
  [CHAIN_ID.BSC]: 'BNB CHAIN',
  [CHAIN_ID.OPTIMISM]: 'OPTIMISM',
  [CHAIN_ID.ARBITRUM]: 'ARBITRUM',
  [CHAIN_ID.AVALANCHE]: 'AVALANCHE',
  [CHAIN_ID.BASE]: 'BASE',
  [CHAIN_ID.PULSE]: 'PULSE',
  [CHAIN_ID.MANTA]: 'MANTA',
  [CHAIN_ID.SONIC]: 'SONIC',
};

export const DEX_SCREENER_LINK: ChainMapped<string> = {
  [CHAIN_ID.BSC]: 'https://dexscreener.com/bsc/',
  [CHAIN_ID.OPTIMISM]: 'https://dexscreener.com/optimism/',
  [CHAIN_ID.ARBITRUM]: 'https://dexscreener.com/arbitrum/',
  [CHAIN_ID.AVALANCHE]: 'https://dexscreener.com/avalanche/',
  [CHAIN_ID.BASE]: 'https://dexscreener.com/base/',
  [CHAIN_ID.PULSE]: 'https://dexscreener.com/pulsechain/',
  [CHAIN_ID.MANTA]: 'https://dexscreener.com/manta/',
  [CHAIN_ID.SONIC]: 'https://dexscreener.com/sonic/',
};

export const CHAIN_INFO: ChainMapped<ChainInfo> = {
  [CHAIN_ID.BSC]: {
    logo: binance,
    inactiveLogo: binanceInactive,
    name: 'BNB Chain',
    walletName: 'Binance Smart Chain',
    blockExplorerUrl: 'https://bscscan.com',
  },
  [CHAIN_ID.OPTIMISM]: {
    logo: optimism,
    inactiveLogo: optimismInactive,
    name: 'OP Mainnet',
    walletName: 'Optimism Mainnet',
    blockExplorerUrl: 'https://optimistic.etherscan.io/',
  },
  [CHAIN_ID.ARBITRUM]: {
    logo: arbitrum,
    inactiveLogo: arbitrumInactive,
    name: 'Arbitrum One',
    walletName: 'Arbitrum One Mainnet',
    blockExplorerUrl: 'https://arbiscan.io',
  },
  [CHAIN_ID.AVALANCHE]: {
    logo: avalanche,
    inactiveLogo: avalancheInactive,
    name: 'Avalanche C-Chain',
    walletName: 'Avalanche Mainnet',
    blockExplorerUrl: 'https://snowscan.xyz',
  },
  [CHAIN_ID.BASE]: {
    logo: base,
    inactiveLogo: baseInactive,
    name: 'Base',
    walletName: 'Base Mainnet',
    blockExplorerUrl: 'https://basescan.org/',
  },
  [CHAIN_ID.PULSE]: {
    logo: pulse,
    inactiveLogo: pulseInactive,
    whiteLogo: pulseWhite,
    name: 'PulseChain',
    walletName: 'Pulse Mainnet',
    blockExplorerUrl:
      'https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy',
  },
  [CHAIN_ID.MANTA]: {
    logo: manta,
    inactiveLogo: mantaInactive,
    whiteLogo: mantaWhite,
    name: 'Manta Pacific',
    walletName: 'Manta Mainnet',
    blockExplorerUrl: 'https://manta.socialscan.io/',
  },
  [CHAIN_ID.SONIC]: {
    logo: sonic,
    inactiveLogo: sonicInactive,
    name: 'Sonic',
    walletName: 'Sonic Mainnet',
    blockExplorerUrl: 'https://explorer.soniclabs.com/',
  },
};

export const TRX_LINK: Record<AvailableChainIdType, string> = {
  // [CHAIN_ID.BSC]: 'https://bscscan.com/tx/',
  // [CHAIN_ID.OPTIMISM]: 'https://optimistic.etherscan.io/tx/',
  // [CHAIN_ID.ARBITRUM]: 'https://arbiscan.io/tx/',
  // [CHAIN_ID.AVALANCHE]: 'https://snowscan.xyz/tx/',
  [CHAIN_ID.BASE]: 'https://basescan.org/tx/',
  // [CHAIN_ID.PULSE]:
  //   'https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/tx/',
  // [CHAIN_ID.MANTA]: 'https://manta.socialscan.io/tx/',
  // [CHAIN_ID.SONIC]: 'https://explorer.soniclabs.com/tx/',
};

export const GAS_MULTIPLIER: ChainMapped<number> = {
  [CHAIN_ID.BSC]: 1.3,
  [CHAIN_ID.OPTIMISM]: 1.3,
  [CHAIN_ID.ARBITRUM]: 1.3,
  [CHAIN_ID.AVALANCHE]: 1.3,
  [CHAIN_ID.BASE]: 1.3,
  [CHAIN_ID.PULSE]: 1.3,
  [CHAIN_ID.MANTA]: 1.3,
  [CHAIN_ID.SONIC]: 1.3,
};

export const MAX_FARMS_COUNT: ChainMapped<number> = {
  [CHAIN_ID.ARBITRUM]: 10,
  [CHAIN_ID.OPTIMISM]: 10,
  [CHAIN_ID.BSC]: 10,
  [CHAIN_ID.AVALANCHE]: 10,
  [CHAIN_ID.BASE]: 10,
  [CHAIN_ID.PULSE]: 10,
  [CHAIN_ID.MANTA]: 10,
  [CHAIN_ID.SONIC]: 10,
};

export const INIT_DEPOSIT_AMOUNT: ChainMapped<string> = {
  [CHAIN_ID.ARBITRUM]: '400000000000000',
  [CHAIN_ID.OPTIMISM]: '300000000000000',
  [CHAIN_ID.SONIC]: '1100000000000000000',
  [CHAIN_ID.BSC]: '2000000000000000',
  [CHAIN_ID.AVALANCHE]: '20000000000000000',
  [CHAIN_ID.PULSE]: '10000000000000000000000',
  [CHAIN_ID.BASE]: '300000000000000',
  [CHAIN_ID.MANTA]: '300000000000000',
};

export const DEFAULT_CHAIN_ID = CHAIN_ID.BASE;

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
